// Core imports
import React, { Fragment, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { t } from 'i18next';
import { Link, useParams } from 'react-router-dom';
import moment from 'moment-timezone';

// Component imports
import { Card, Col, Divider, Empty, Row, Select, Space, Tag, Tooltip, Typography } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';

// Misc imports
import { isEmptyArray } from '../../utils/is/isEmptyArray';
import { COLOR_STATUS_TYPES, DATETIME_BACKEND_FORMAT, DEFAULT_DATETIME_FORMAT, LANGUAGE_OPTIONS } from '../../constants/main';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import replaceUrlParams from '../../utils/string/replaceUrlParams';
import { NAV_CHAT_SESSIONS } from '../../constants/navigation';

// Const

const SESSION_STATUSES = {
  PASSED: 'Passed',
  FAILED: 'Failed'
};

const formatDate = (date, timezone = 'UTC') => {
  return moment(date).tz(timezone).format(DEFAULT_DATETIME_FORMAT);
};

const formatBackendDate = (date, timezone= 'UTC') => {
  if(!date) {
    return moment.tz(moment(Date.now()), timezone).endOf('day').format(DATETIME_BACKEND_FORMAT);
  }
  const currentDate = moment.tz(moment(date), timezone).startOf('day').format(DATETIME_BACKEND_FORMAT);
  return currentDate;
};

const AutomatedAgentTestsResultsView = ({
  activeAutomatedTest = {}
}) => {

  const { orgId } = useParams();
  const { data: organization } = getCurrentQueryState('fetchOrganization', { orgId });

  const sortedResultSessions = useMemo(() => {
    if (!activeAutomatedTest?.resultSessions?.length) {
      return [];
    }
    return [...activeAutomatedTest.resultSessions]
      .map(session => ({ ...session, timestamp: new Date(session.date).getTime() }))
      .sort((a, b) => b.timestamp - a.timestamp);
  }, [activeAutomatedTest.resultSessions]);

  const [selectedTestResultDate, setSelectedTestResultDate] = useState(sortedResultSessions?.[0]?.date || null);

  const testResultsMap = useMemo(() => {
    return new Map(sortedResultSessions.length > 0 ? sortedResultSessions.map(result => [result.date, result]) : []);
  }, [sortedResultSessions]);

  const activeResult = useMemo(() => {
    return testResultsMap.get(selectedTestResultDate);
  }, [selectedTestResultDate, testResultsMap]);

  const renderSelectOptionLabel = (date, timezone = 'UTC') => (
    <Typography.Text className="fs-base" strong>
      {`${formatDate(date, timezone)}`}
    </Typography.Text>
  );

  const selectOptions = useMemo(() => {
    if(!sortedResultSessions?.length) {
      return;
    }

    return sortedResultSessions?.map((result) => (
      <Select.Option
        key={`${result.date}`}
        value={result.date}
        label={
          <Fragment>
            {renderSelectOptionLabel(result?.date, organization?.timezone)}
          </Fragment>
        }
      >
        {renderSelectOptionLabel(result?.date, organization?.timezone)}
      </Select.Option>
    ));
  }, [sortedResultSessions, organization?.timezone]);

  const renderSuccessRateTag = (successRate) => {

    let color;
    if (successRate >= 80) {
      color = COLOR_STATUS_TYPES.SUCCESS;
    } else if (successRate >= 70) {
      color = COLOR_STATUS_TYPES.WARNING;
    } else {
      color = COLOR_STATUS_TYPES.ERROR;
    }

    return <Tag color={color} className="fs-xxl d-flex justify-content-center align-items-center h-100 w-100 border-radius-lg">
      <Typography.Text className={`text-${color} fw-600`} >
        {`${successRate % 1 === 0 ? successRate : successRate?.toFixed(2)}% of Sessions Passed`}
      </Typography.Text>
    </Tag>;

  };

  if(!activeResult || isEmptyArray(activeResult?.resultSessions)) {
    return <Empty description={t('Please run a test to generate results. Once the test is complete, your data will be shown here.')} />;
  }

  return (
    <Row>
      <Col xs={24}>
        <Row className="pb-10">
          <Col xs={6}>
            <Select
              className="w-100"
              style={{ minWidth: '230px' }}
              placeholder={t('Please select a Test Run')}
              getPopupContainer={triggerNode => triggerNode.parentElement}
              size="large"
              popupClassName="scrollbar-custom overflow-y-auto missed-question-details-dropdown"
              optionLabelProp="label"
              defaultActiveFirstOption
              value={selectedTestResultDate}
              onSelect={(value) => {
                setSelectedTestResultDate(value);
              }}
            >
              {selectOptions}
            </Select>
          </Col>
        </Row>
        <Row>
          <Col xs={16}>
            <Divider orientation="left" className="m-0 pr-2 py-2">
              <Typography.Text>
                {t('Overall')}
                <Tooltip
                  className="ml-1"
                  overlayStyle={{ maxWidth: 350 }}
                  title={t('Average outcome of all sessions, shown as the percentage that passed.')}
                >
                  <QuestionCircleOutlined className="v-align-top fs-base" />
                </Tooltip>
              </Typography.Text>
            </Divider>
          </Col>
          <Col xs={8}>
            <Divider orientation="left" className="m-0 pr-2 py-2">
              <Typography.Text>
                {t('Test Details')}
                <Tooltip
                  className="ml-1"
                  overlayStyle={{ maxWidth: 350 }}
                  title={t(`Quick reference of the test's setup and metadata.`)}
                >
                  <QuestionCircleOutlined className="v-align-top fs-base" />
                </Tooltip>
              </Typography.Text>
            </Divider>
          </Col>
        </Row>
        <Row>
          <Col xs={16}>
            <div className="d-flex justify-content-center align-items-center w-100 h-100">
              {renderSuccessRateTag(activeResult?.successRate)}
            </div>
          </Col>
          <Col xs={8}>
            <Card
              bordered={false}
              bodyStyle={{ height: '100%', overflowY: 'auto', maxHeight: '400px' }}
              className="shadow-none border-none bg-layout-gray h-100"
            >
              <ul style={{ listStyleType: 'disc' }} className="m-0 pl-2">
                <li className="mb-2">
                  <Typography.Text>{activeResult?.resultDetails?.testName || 'N/A'}</Typography.Text>
                </li>
                <li className="mb-2">
                  <Typography.Text>{activeResult?.resultDetails?.testDescription || 'N/A'}</Typography.Text>
                </li>
                <li className="mb-2">
                  <Typography.Text>
                    <Space size="small">
                      {LANGUAGE_OPTIONS.find(lang => lang.key === activeResult?.resultDetails?.languageCode)?.label}
                    </Space>
                  </Typography.Text>
                </li>
                <li className="mb-2">
                  <Typography.Text>{activeResult?.resultDetails?.examinedTenantName || 'N/A'}</Typography.Text>
                </li>
                <li className="mb-2">
                  <Typography.Text>{activeResult?.resultDetails?.flowName || 'N/A'}</Typography.Text>
                </li>
                <li className="mb-2">
                  <Typography.Text>
                    {activeResult ? formatDate(activeResult.date, organization?.timezone) : 'N/A'}
                  </Typography.Text>
                </li>
              </ul>
            </Card>
          </Col>
        </Row>
        <Row className="pt-5">
          <Col xs={24}>
            <Divider orientation="left" className="m-0 pr-2 py-2">
              <Typography.Text>
                {t('Detailed Results')}
                <Tooltip
                  className="ml-1"
                  overlayStyle={{ maxWidth: 350 }}
                  title={t('Per-session outcomes with reasons for pass or fail.')}
                >
                  <QuestionCircleOutlined className="v-align-top fs-base" />
                </Tooltip>
              </Typography.Text>
            </Divider>
          </Col>
          <Col xs={24}>
            <Row className="border-bottom">
              <Col xs={8} className="d-flex justify-content-center align-items-center">
                <Typography.Text className="fw-bold py-2">
                  {t('Session ID')}
                </Typography.Text>
              </Col>
              <Col xs={4} className="d-flex justify-content-center align-items-center">
                <Typography.Text className="fw-bold py-2">
                  {t('Result')}
                </Typography.Text>
              </Col>
              <Col xs={12} className="d-flex justify-content-center align-items-center">
                <Typography.Text className="fw-bold py-2">
                  {t('Explanation')}
                </Typography.Text>
              </Col>
            </Row>
            {
              activeResult?.results?.map(result => {
                const color = result?.passed ? COLOR_STATUS_TYPES.SUCCESS : COLOR_STATUS_TYPES.ERROR;
                const label = result?.passed ? SESSION_STATUSES.PASSED : SESSION_STATUSES.FAILED;

                return (
                  <Row className="border-bottom">
                    <Col xs={8} className="d-flex justify-content-center align-items-center">
                      {
                        !activeResult?.resultDetails?.examinedTenantId ? (
                          <Typography.Text className="py-2">
                            {result?.resultId}
                          </Typography.Text>
                        ) : (
                          <Link
                            to={replaceUrlParams(NAV_CHAT_SESSIONS, { orgId }) + `?startDate=${encodeURIComponent(`${formatBackendDate(activeResult.date, organization?.timezone)}`)}&endDate=${encodeURIComponent(`${formatBackendDate(null, organization?.timezone)}`)}&searchBy=uuid&searchTerm=&page=0&pageSize=10&sortBy=createdAt&order=DESC&uuid=${result?.resultId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`py-2`}
                          >
                            {result?.resultId}
                          </Link>
                        )
                      }
                    </Col>
                    <Col xs={4} className="d-flex justify-content-center align-items-center">
                      <Tag color={color} className="fs-lg d-flex justify-content-center align-items-center h-100 w-100 border-radius-0 mr-0">
                        <Typography.Text className={`text-${color} py-2 fw-600`} >
                          {t(label)}
                        </Typography.Text>
                      </Tag>
                    </Col>
                    <Col xs={12} className="d-flex justify-content-center align-items-center">
                      <Typography.Text className={`p-2`} >
                        {result?.reason}
                      </Typography.Text>
                    </Col>
                  </Row>
                );
              })
            }
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

AutomatedAgentTestsResultsView.propTypes = {
  activeAutomatedTest: PropTypes.object
};

export default AutomatedAgentTestsResultsView;
